import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  FaTelegram, 
  FaRocket, 
  FaCode, 
  FaBook,
  FaQuestion,
  FaChevronRight,
  FaExternalLinkAlt,
  FaDownload,
  FaPlay,
  FaCar,
  FaUsers
} from 'react-icons/fa';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <FaRocket className="h-5 w-5" />
    },
    {
      id: 'rider-commands',
      title: 'Rider Bot Commands',
      icon: <FaUsers className="h-5 w-5" />
    },
    {
      id: 'driver-commands',
      title: 'Driver Bot Commands',
      icon: <FaCar className="h-5 w-5" />
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: <FaQuestion className="h-5 w-5" />
    }
  ];

  const riderCommands = [
    { command: '/start', description: 'Initialize the bot and display main menu' },
    { command: '/me', description: 'Display complete rider profile information' },
    { command: '/riderequest', description: 'Request a new ride with pickup, dropoff, time, and bid' },
    { command: '/cancelride', description: 'Cancel active ride request with confirmation' },
    { command: '/completed', description: 'Mark current ride as completed' },
    { command: '/rides', description: 'View ride history and active requests' },
    { command: '/update', description: 'Update profile information (name, phone, addresses)' },
    { command: '/erase', description: 'Permanently delete rider profile with confirmation' },
    { command: '/help', description: 'Display comprehensive command guide' },
    { command: '/time', description: 'Show current date/time with formatting examples' },
    { command: '/clearcache', description: 'Clear user session cache' }
  ];

  const driverCommands = [
    { command: '/start', description: 'Initialize bot and display main driver menu' },
    { command: '/me', description: 'Display complete driver profile and availability status' },
    { command: '/available', description: 'Set availability with location, radius, and duration' },
    { command: '/unavailable', description: 'Stop accepting new ride requests' },
    { command: '/completed', description: 'Mark current matched ride as completed' },
    { command: '/canceled', description: 'Cancel current matched ride with confirmation' },
    { command: '/rides', description: 'View complete ride history and statistics' },
    { command: '/stats', description: 'View comprehensive driver performance statistics' },
    { command: '/update', description: 'Update profile (name, phone, vehicle, license plate)' },
    { command: '/erase', description: 'Permanently delete driver profile with confirmation' },
    { command: '/help', description: 'Display comprehensive command guide' },
    { command: '/time', description: 'Show current date/time with formatting examples' },
    { command: '/clearcache', description: 'Clear driver session cache' }
  ];



  const faqs = [
    {
      question: "How do I get started with Boulevard?",
      answer: "Choose your role: message @Rideatease_bot for riders or @Rideeasedriver_bot for drivers on Telegram. Send /start and follow the registration process to create your profile."
    },
    {
      question: "Does Boulevard really charge zero fees?",
      answer: "Yes! Boulevard is a community service by iautenticai company. We charge 0% platform fees. Drivers keep 100% of their earnings, and riders pay only what they offer."
    },
    {
      question: "How does ride matching work?",
      answer: "Riders create requests with pickup, dropoff, time, and bid amount. Drivers set their availability with location and radius. Our system automatically matches nearby requests with available drivers."
    },
    {
      question: "Can I use natural language with the bots?",
      answer: "Yes! Both bots support natural language. You can say things like 'I need a ride from Miami Airport to downtown at 6pm for $25' or 'I'm available at downtown Miami, 5 miles, 2 hours'."
    },
    {
      question: "How do I set my availability as a driver?",
      answer: "Use /available with your location, service radius, and duration. Example: 'I'm available at 123 Main St Miami, radius 10 miles, for 3 hours'. The bot will start matching you with nearby ride requests."
    },
    {
      question: "What if I need to cancel a ride?",
      answer: "Both riders and drivers can cancel rides using /cancelride or /canceled respectively. The system will ask for confirmation and notify the other party. Frequent cancellations may affect your rating."
    },
    {
      question: "How do payments work?",
      answer: "Riders set their bid amount when requesting rides. Drivers see this amount and can accept or decline. Payment happens directly between rider and driver - Boulevard takes no fees or commissions."
    },
    {
      question: "Can I update my profile information?",
      answer: "Yes! Use /update to modify your name, phone, addresses (for riders), or vehicle details (for drivers). You can also use natural language like 'Update my phone to 555-0123'."
    },
    {
      question: "How do I get help or support?",
      answer: "Use /help in either bot for comprehensive command guides and tips. For additional support, visit our website contact form or check this documentation page."
    },
    {
      question: "Is my data safe and private?",
      answer: "Yes. As a community service, we don't sell your data or use it for advertising. We only share information when legally required for criminal investigations. See our Privacy Policy for details."
    }
  ];



  return (
    <>
      <Helmet>
        <title>Documentation - Boulevard</title>
        <meta name="description" content="Complete documentation for Boulevard including Telegram bot commands, getting started guide, and frequently asked questions for riders and drivers." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Documentation
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to know about using Boulevard's community ride-sharing service, 
              from getting started with our Telegram bots to advanced features.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Documentation Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all duration-200 flex items-center space-x-3 ${
                      activeSection === section.id
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {section.icon}
                    <span>{section.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {activeSection === 'getting-started' && (
                  <div className="prose prose-lg max-w-none">
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
                      Getting Started with RideEase
                    </h2>
                    
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                      <div className="flex">
                        <FaRocket className="text-blue-400 mt-1 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold text-blue-800 mb-2">
                            Quick Start
                          </h3>
                          <p className="text-blue-700">
                            Get up and running with Boulevard in less than 5 minutes!
                          </p>
                        </div>
                      </div>
                    </div>

                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                      Step 1: Access the Telegram Bot
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Boulevard has two separate Telegram bots - one for riders and one for drivers:
                    </p>
                    <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mb-6">
                      <a
                        href="https://t.me/Rideatease_bot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <FaTelegram />
                        <span>Rider Bot</span>
                        <FaExternalLinkAlt className="text-sm" />
                      </a>
                      <a
                        href="https://t.me/Rideeasedriver_bot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <FaTelegram />
                        <span>Driver Bot</span>
                        <FaExternalLinkAlt className="text-sm" />
                      </a>
                    </div>

                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                      Step 2: Initialize Your Account
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-600 mb-6">
                      <li>Send <code>/start</code> to your chosen bot</li>
                      <li>Register with your basic information (name, phone number)</li>
                      <li>For drivers: Add vehicle details (color, license plate)</li>
                      <li>For riders: Optionally set home and work addresses</li>
                      <li>Start using the service!</li>
                    </ol>

                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                      Step 3: Start Using Boulevard
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-green-50 p-6 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">For Drivers</h4>
                        <ul className="text-green-700 space-y-1">
                          <li>• Set availability with <code>/available</code></li>
                          <li>• Accept incoming ride requests</li>
                          <li>• Complete rides with <code>/completed</code></li>
                          <li>• Keep 100% of your earnings!</li>
                        </ul>
                      </div>
                      <div className="bg-blue-50 p-6 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">For Riders</h4>
                        <ul className="text-blue-700 space-y-1">
                          <li>• Request rides with <code>/riderequest</code></li>
                          <li>• Set your own fair price</li>
                          <li>• Get matched with nearby drivers</li>
                          <li>• Pay only what you offered!</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'rider-commands' && (
                  <div>
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
                      Rider Bot Commands (@Rideatease_bot)
                    </h2>
                    
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">
                        Getting Started as a Rider
                      </h3>
                      <p className="text-blue-700">
                        Start by messaging <a href="https://t.me/Rideatease_bot" target="_blank" rel="noopener noreferrer" 
                        className="font-semibold underline">@Rideatease_bot</a> on Telegram and use /start to begin.
                      </p>
                    </div>

                    <div className="space-y-6">
                      {riderCommands.map((cmd, index) => (
                        <div key={index} className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-400">
                          <code className="text-blue-600 font-semibold text-xl">
                            {cmd.command}
                          </code>
                          <p className="text-gray-700 mt-3 text-lg">{cmd.description}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-12 bg-green-50 border-l-4 border-green-400 p-6">
                      <h4 className="text-lg font-semibold text-green-800 mb-3">
                        Natural Language Support
                      </h4>
                      <p className="text-green-700 mb-3">
                        The rider bot supports natural language! You can say things like:
                      </p>
                      <ul className="text-green-700 space-y-2">
                        <li>• "I need a ride from Miami Airport to downtown at 6pm for $25"</li>
                        <li>• "Take me from home to work at 8am, bid $20"</li>
                        <li>• "Update my phone to 555-0123"</li>
                      </ul>
                    </div>
                  </div>
                )}

                {activeSection === 'driver-commands' && (
                  <div>
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
                      Driver Bot Commands (@Rideeasedriver_bot)
                    </h2>
                    
                    <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-8">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">
                        Getting Started as a Driver
                      </h3>
                      <p className="text-green-700">
                        Start by messaging <a href="https://t.me/Rideeasedriver_bot" target="_blank" rel="noopener noreferrer" 
                        className="font-semibold underline">@Rideeasedriver_bot</a> on Telegram and use /start to begin.
                      </p>
                    </div>

                    <div className="space-y-6">
                      {driverCommands.map((cmd, index) => (
                        <div key={index} className="bg-gray-50 p-6 rounded-lg border-l-4 border-green-400">
                          <code className="text-green-600 font-semibold text-xl">
                            {cmd.command}
                          </code>
                          <p className="text-gray-700 mt-3 text-lg">{cmd.description}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-12 bg-blue-50 border-l-4 border-blue-400 p-6">
                      <h4 className="text-lg font-semibold text-blue-800 mb-3">
                        Availability Management
                      </h4>
                      <p className="text-blue-700 mb-3">
                        Set your availability with natural language:
                      </p>
                      <ul className="text-blue-700 space-y-2">
                        <li>• "I'm available at 1251 E Sunrise Blvd, Fort Lauderdale, radius 10 miles, for 3 hours"</li>
                        <li>• "Available at downtown Miami, 5 miles, 2 hours"</li>
                        <li>• "123 Main Street Miami" (bot will ask for radius and duration)</li>
                      </ul>
                    </div>

                    <div className="mt-12 bg-yellow-50 border-l-4 border-yellow-400 p-6">
                      <div className="flex">
                        <FaBook className="text-yellow-400 mt-1 mr-3" />
                        <div>
                          <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                            Pro Tips
                          </h4>
                          <ul className="text-yellow-700 space-y-1">
                            <li>• Use <code>/help</code> anytime to get context-sensitive assistance</li>
                            <li>• Commands are case-insensitive and work with or without the slash</li>
                            <li>• You can cancel most operations by typing "cancel" or "stop"</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}



                {activeSection === 'faq' && (
                  <div>
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
                      Frequently Asked Questions
                    </h2>
                    
                    <div className="space-y-6">
                      {faqs.map((faq, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start">
                            <FaQuestion className="text-primary-600 mt-1 mr-3 flex-shrink-0" />
                            {faq.question}
                          </h3>
                          <p className="text-gray-600 leading-relaxed pl-8">
                            {faq.answer}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-12 text-center">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                        Still have questions?
                      </h3>
                      <p className="text-gray-600 mb-6">
                        We're here to help! Contact our support team for personalized assistance.
                      </p>
                      <a
                        href="/contact"
                        className="btn-primary inline-flex items-center space-x-2"
                      >
                        <span>Contact Support</span>
                        <FaChevronRight />
                      </a>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>


    </>
  );
};

export default Documentation;
